import { useState } from 'react'
import { cn, formatPrice } from '@/lib/utils'
import type { Perfume, SizeOption } from '@/types'

interface SizePriceSelectorProps {
  perfume: Perfume
  selectedSizeId: string
  onSelect: (size: SizeOption) => void
}

export function SizePriceSelector({ perfume, selectedSizeId, onSelect }: SizePriceSelectorProps) {
  const [hovered, setHovered] = useState<string | null>(null)

  return (
    <div className="space-y-3">
      {perfume.sizes.map((size) => {
        const isSelected = selectedSizeId === size.id
        const ml = parseInt(size.size.match(/\d+/)?.[0] ?? '1', 10)
        const perMl = size.price / ml
        const outOfStock = size.inStock === false
        return (
          <button
            key={size.id}
            disabled={outOfStock}
            onClick={() => onSelect(size)}
            onMouseEnter={() => setHovered(size.id)}
            onMouseLeave={() => setHovered(null)}
            className={cn(
              'flex w-full items-center justify-between border px-4 py-3 text-left transition-all',
              outOfStock
                ? 'cursor-not-allowed border-ink/10 bg-wd-mist/50'
                : isSelected
                  ? 'border-ink bg-wd-mushroom'
                  : 'border-ink/10 hover:border-ink/40'
            )}
          >
            <div>
              <span
                className={cn(
                  'block text-sm',
                  outOfStock ? 'text-carbon/40' : isSelected ? 'text-carbon' : 'text-carbon/80'
                )}
              >
                {size.size}
              </span>
              {size.isRefillable && !outOfStock && (
                <span className="text-xs uppercase tracking-[0.15em] text-carbon/50">Refillable</span>
              )}
              {(hovered === size.id || isSelected) && size.price > 10 && !outOfStock && (
                <span className="mt-1 block text-xs text-carbon/50">{formatPrice(perMl)} / ml</span>
              )}
            </div>
            <div className="text-right">
              <span
                className={cn(
                  'block font-display text-lg',
                  outOfStock ? 'text-carbon/40 line-through' : 'text-carbon'
                )}
              >
                {formatPrice(size.price)}
              </span>
              {outOfStock && (
                <span className="block text-xs uppercase tracking-[0.15em] text-wd-orange">
                  Out of stock
                </span>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}
